import { unref } from 'vue';
import { Router as VueRouter, RouteLocationNormalized } from 'vue-router';
import { isEmpty, omitBy } from '@core/utilities/lodash-utils';
import { CreateHistoryRouterParams, Router } from './types';
import type { Filter, Location } from '../../types/filters';
import type { SearchRouteState } from '../../types/catalog';
import { URLManager } from '../url-manager/types';
import { SearchCategory } from '@monolith/legacy/types/api/category';

type Range = { min: number; max: number };

export abstract class HistoryRouterBase implements Router<SearchRouteState> {
  protected readonly router: VueRouter;
  protected readonly getBasePath?: () => string;
  protected readonly getLang: () => string;
  protected readonly getCategories: () => Record<string, SearchCategory>;
  protected readonly getLocations: () => Location[];
  protected readonly getCurrency: () => string;
  protected readonly urlManager: () => URLManager;
  // Extra (non-utm) query params the consumer wants carried over when the catalog rewrites
  // the URL from its own state. Defaults to none.
  protected readonly preserveParams: string[];

  private removeAfterEach: () => void;
  private isDisposed = false;

  constructor(params: CreateHistoryRouterParams) {
    this.router = params.router;
    this.getBasePath = params.getBasePath;
    this.getLang = params.getLang;
    this.getCategories = params.getCategories;
    this.getLocations = params.getLocations;
    this.getCurrency = params.getCurrency;
    this.urlManager = params.urlManager;
    this.preserveParams = params.preserveParams ?? [];
  }

  public abstract read(): SearchRouteState;

  public createURL(routeState: SearchRouteState): string {
    const { path, hash } = this.currentRoute;
    const basePath = this.getBasePath?.() ?? path;
    const hashParams = this.getHashParams(this.router.currentRoute.value);
    return this.urlManager().createURL(
      {
        textQuery: routeState.q,
        selectedCategories: this.getSelectedCategories(routeState, this.getCategories()),
        selectedDiscounts: this.getSelectedFilters(routeState, 'discount', true),
        selectedMaxDiscountRates: this.getSelectedFilters(routeState, 'max_discount_rate.range', true),
        selectedLocations: this.getSelectedLocations(routeState, this.getLocations()),
        selectedMadeIns: this.getSelectedFilters(routeState, 'made_in'),
        selectedMargins: this.getSelectedFilters(routeState, 'margin'),
        selectedValues: this.getSelectedFilters(routeState, 'tags'),
        selectedShipping: this.getSelectedFilters(routeState, 'shipping_lead_time'),
        currentPageIndex: routeState.page ? routeState.page - 1 : 0,
        retailPriceRange: this.getSelectedRetailPriceRange(routeState),
        selectedSorting: undefined,
        selectedView: hash?.includes('view=1') ? 1 : 0,
        selectedFilterBrands: this.getSelectedFilters(routeState, 'brand.name'),
        selectedLoyaltyOffer: this.getSelectedFilters(routeState, 'aks_plus'),
        aksSystems: this.getSelectedFilters(routeState, 'aks_systems'),
        selectedStorageTemperature: this.getSelectedFilters(routeState, 'storage_temperature'),
        selectedAlcoholContent: this.getSelectedFilters(routeState, 'alcohol_content'),
        selectedProductTypes: this.getSelectedFilters(routeState, 'product_type.id'),
        attributes: this.getAttributeFilters(routeState),
        sortBy: hashParams.get('sortBy'),
      },
      basePath
    );
  }

  public write(routeState: SearchRouteState) {
    if (this.isDisposed) {
      return;
    }

    const url = this.preservePassthroughParams(this.createURL(routeState));
    if (this.currentRoute.fullPath !== url) {
      this.router.push(url);
    }
  }

  // Carry over query params the catalog doesn't own so its URL rewrites don't drop them:
  // tracking (utm_*) plus any extra params the consumer asked to preserve (preserveParams).
  private preservePassthroughParams(url: string): string {
    const currentQuery = new URLSearchParams(this.currentRoute.query as Record<string, string>);
    const preserved = new URLSearchParams();

    currentQuery.forEach((value, key) => {
      if (key.startsWith('utm_') || this.preserveParams.includes(key)) {
        preserved.set(key, value);
      }
    });

    if (!preserved.toString()) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${preserved.toString()}`;
  }

  public onUpdate(cb: (route: SearchRouteState) => void) {
    if (!this.envHasWindow() || this.isDisposed) {
      return;
    }

    this.removeAfterEach = this.router.afterEach(() => {
      cb(this.read());
    });
  }

  public dispose() {
    this.removeAfterEach?.();
    this.isDisposed = true;
  }

  public envHasWindow() {
    return typeof window !== 'undefined';
  }

  protected removeEmptyValues(obj: Record<string, unknown>): Record<string, unknown> {
    return omitBy(obj, isEmpty);
  }

  protected getHashParams(route: RouteLocationNormalized) {
    return new URLSearchParams(route.fullPath.split('#')[1]);
  }

  protected get currentRoute(): RouteLocationNormalized {
    return unref(this.router.currentRoute);
  }

  protected getAttributeRefinements(hashParams: URLSearchParams) {
    return this.getAttributeValues(
      hashParams,
      (key) => key.startsWith('attributes') && !key.endsWith('number') && !key.endsWith('boolean'),
      (value) => value.split(',')
    );
  }

  protected getAttributeRanges(hashParams: URLSearchParams) {
    return this.getAttributeValues(hashParams, (key) => key.startsWith('attributes') && key.endsWith('number'));
  }

  protected getAttributeToggles(hashParams: URLSearchParams) {
    return this.getAttributeValues(hashParams, (key) => key.startsWith('attributes') && key.endsWith('boolean'));
  }

  private getAttributeValues(
    hashParams: URLSearchParams,
    keyCondition: (key: string) => boolean,
    valueIterator: (value: string) => string | string[] = (value) => value
  ): Record<string, string | string[]> {
    return Object.fromEntries(
      Array.from(hashParams.entries())
        .filter(([key]) => keyCondition(key))
        .map(([key, value]) => [key, valueIterator ? valueIterator(value) : value.split(',')])
    );
  }

  // The substring fallback exists for keys that carry a lang/currency suffix (e.g. 'made_in'
  // must still match 'made_in.fr'). But 'discount' is also a substring of the unrelated
  // 'max_discount_rate.range' key ('max_DISCOUNT_rate.range'), so the substring fallback
  // matches it even when no literal 'discount' key exists at all (e.g. only
  // max_discount_rate.range is set) — preferring an exact match first isn't enough since
  // there's nothing to prefer it over. `exact: true` disables the fallback entirely for
  // callers where the key never carries a suffix, instead of narrowing the fallback for
  // every other caller that still needs it.
  private getValuesOfKey<T = string[]>(obj: Record<string, T>, keyName: string, defaultValue: T, exact = false): T {
    const entries = Object.entries(obj ?? {});
    const exactMatch = entries.find(([key]) => key === keyName);
    if (exactMatch || exact) {
      return exactMatch?.[1] ?? defaultValue;
    }

    return entries.find(([key]) => key.includes(keyName))?.[1] ?? defaultValue;
  }

  private getSelectedCategories(routeState: SearchRouteState, categories: Record<string, SearchCategory> = {}): SearchCategory[] {
    const categoryIds: string[] = this.getValuesOfKey(routeState.hierarchicalMenu, 'categories', []);

    return categoryIds.map((id) => categories[id]).filter(Boolean);
  }

  private getSelectedFilters(
    routeState: SearchRouteState,
    filterName: keyof SearchRouteState['refinementList'],
    exact = false
  ): Filter[] {
    const filterValues: string[] = this.getValuesOfKey(routeState.refinementList, filterName.toString(), [], exact);

    return filterValues.map((id) => ({ id, label: id }));
  }

  private getSelectedLocations(routeState: SearchRouteState, locations: Location[] = []): Location[] {
    const locationIds: string[] = this.getValuesOfKey(routeState.refinementList, 'location', []);

    return locationIds.map((id) => locations.find((location) => location.id === id)).filter(Boolean);
  }

  private getSelectedRetailPriceRange(routeState: SearchRouteState): Range {
    const [min, max] = this.getValuesOfKey(routeState.range, 'retail_prices', '').split(':');
    const minValue = min ? parseInt(min, 10) : undefined;
    const maxValue = max ? parseInt(max, 10) : undefined;

    return { min: minValue, max: maxValue };
  }

  private getAttributeFilters(routeState: SearchRouteState): Filter[] {
    const attributes = [];

    if (routeState.refinementList) {
      attributes.push(...Object.entries(routeState.refinementList).filter(([key]) => key.includes('attributes')));
    }

    if (routeState.range) {
      attributes.push(...Object.entries(routeState.range).filter(([key]) => key.includes('attributes')));
    }

    if (routeState.toggle) {
      attributes.push(...Object.entries(routeState.toggle).filter(([key]) => key.includes('attributes')));
    }

    return attributes.map(([key, values]) => {
      const value = Array.isArray(values) ? values.join(',') : values;

      return { id: key, label: value };
    });
  }
}
